import React, { useState } from 'react';
import {
  Plus,
  Copy,
  Eye,
  EyeOff,
  Trash2,
  Key,
  CheckCircle,
  Calendar,
  Activity,
  AlertCircle
} from 'lucide-react';

const APIKeysManagement = () => {
  const [showKey, setShowKey] = useState({});
  const [copied, setCopied] = useState(null);

  const apiKeys = [
    {
      id: 1,
      name: 'Production API Key',
      key: 'sk_live_4xF8gH2jK9mL3nP5qR7sT9vX2yZ4aC6b',
      prefix: 'sk_live_',
      created: '2025-11-15',
      lastUsed: '2 hours ago',
      requests: 45230,
      status: 'active',
      company: 'Acme Corp',
      scopes: ['read', 'write', 'admin']
    },
    {
      id: 2,
      name: 'Development API Key',
      key: 'sk_test_1aB2cD3eF4gH5iJ6kL7mN8oP9qR0sT1u',
      prefix: 'sk_test_',
      created: '2025-12-01',
      lastUsed: '5 min ago',
      requests: 12890,
      status: 'active',
      company: 'TechStart Inc',
      scopes: ['read', 'write']
    },
    {
      id: 3,
      name: 'Webhook Integration',
      key: 'wh_secret_2vW3xY4zA5bC6dE7fG8hI9jK0lM1nO2p',
      prefix: 'wh_secret_',
      created: '2026-01-10',
      lastUsed: 'Never',
      requests: 0,
      status: 'inactive',
      company: 'BuildCo',
      scopes: ['webhooks']
    },
    {
      id: 4,
      name: 'Analytics Read-Only',
      key: 'sk_readonly_3qP4rQ5sR6tS7uT8vU9wV0xW1yX2zA3b',
      prefix: 'sk_readonly_',
      created: '2026-01-18',
      lastUsed: '1 day ago',
      requests: 8450,
      status: 'active',
      company: 'DataFlow',
      scopes: ['read']
    },
  ];

  const stats = [
    { name: 'Total API Keys', value: '34', icon: Key, color: 'bg-blue-500' },
    { name: 'Active Keys', value: '28', icon: CheckCircle, color: 'bg-green-500' },
    { name: 'Total Requests', value: '1.2M', icon: Activity, color: 'bg-purple-500' },
    { name: 'Rate Limit Hits', value: '156', icon: AlertCircle, color: 'bg-orange-500' },
  ];

  const handleCopy = (key, id) => {
    navigator.clipboard.writeText(key);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const toggleShowKey = (id) => {
    setShowKey(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const maskKey = (key) => {
    const parts = key.split('_');
    if (parts.length >= 2) {
      const prefix = parts.slice(0, -1).join('_') + '_';
      const secret = parts[parts.length - 1];
      return prefix + secret.slice(0, 8) + '•'.repeat(secret.length - 8);
    }
    return key.slice(0, 12) + '•'.repeat(key.length - 12);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <CheckCircle className="w-3 h-3" />
            Active
          </span>
        );
      case 'inactive':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
            <AlertCircle className="w-3 h-3" />
            Inactive
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">API Keys Management</h1>
          <p className="text-gray-600 mt-1">Manage platform API keys and access tokens</p>
        </div>
        <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Generate New Key
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-sm text-gray-600">{stat.name}</div>
                  <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* API Keys List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="divide-y divide-gray-200">
          {apiKeys.map((apiKey) => (
            <div key={apiKey.id} className="p-6 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <Key className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{apiKey.name}</h3>
                      <p className="text-sm text-gray-600">{apiKey.company}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(apiKey.status)}
                  <button className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* API Key Display */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <code className="flex-1 text-sm font-mono text-gray-900">
                    {showKey[apiKey.id] ? apiKey.key : maskKey(apiKey.key)}
                  </code>
                  <button
                    onClick={() => toggleShowKey(apiKey.id)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
                  >
                    {showKey[apiKey.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => handleCopy(apiKey.key, apiKey.id)}
                    className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                  >
                    {copied === apiKey.id ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {copied === apiKey.id && (
                  <p className="text-xs text-green-600 font-medium">Copied to clipboard!</p>
                )}
              </div>

              {/* Metadata */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-gray-500 mb-1">Created</div>
                  <div className="flex items-center gap-1 text-gray-900">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    {new Date(apiKey.created).toLocaleDateString()}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 mb-1">Last Used</div>
                  <div className="flex items-center gap-1 text-gray-900">
                    <Activity className="w-4 h-4 text-gray-400" />
                    {apiKey.lastUsed}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 mb-1">Total Requests</div>
                  <div className="font-semibold text-gray-900">{apiKey.requests.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-gray-500 mb-1">Scopes</div>
                  <div className="flex gap-1">
                    {apiKey.scopes.map((scope) => (
                      <span
                        key={scope}
                        className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium"
                      >
                        {scope}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* API Documentation */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">API Usage Guidelines</h3>
          <div className="space-y-3 text-sm text-gray-600">
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p>Keep your API keys secure and never share them publicly</p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p>Rotate keys regularly for enhanced security</p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p>Use different keys for production and development environments</p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p>Monitor API usage to detect unusual activity</p>
            </div>
          </div>
          <button className="mt-6 w-full px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors font-medium">
            View API Documentation
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Rate Limits</h3>
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Requests per minute</span>
                <span className="text-sm font-bold text-gray-900">100</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full" style={{ width: '45%' }}></div>
              </div>
              <p className="text-xs text-gray-500 mt-1">45 of 100 used</p>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Requests per hour</span>
                <span className="text-sm font-bold text-gray-900">5,000</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-green-600 h-2 rounded-full" style={{ width: '28%' }}></div>
              </div>
              <p className="text-xs text-gray-500 mt-1">1,400 of 5,000 used</p>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Requests per day</span>
                <span className="text-sm font-bold text-gray-900">100,000</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-purple-600 h-2 rounded-full" style={{ width: '62%' }}></div>
              </div>
              <p className="text-xs text-gray-500 mt-1">62,000 of 100,000 used</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APIKeysManagement;
